export type FallbackChain = {
  primary: string;
  fallbackChain: string[];
  maxRetries?: number;
};

type ProviderTrip = { failures: number; trippedUntil: number };

export interface Fallback {
  execute<T>(chain: FallbackChain, fn: (model: string) => Promise<T>): Promise<T>;
}

function isRetriableError(error: unknown): boolean {
  if (error && typeof error === "object") {
    const e = error as { status?: number; code?: string };
    if (typeof e.status === "number" && e.status >= 400) return true;
    if (e.code === "ECONNRESET" || e.code === "ETIMEDOUT") return true;
  }
  if (error instanceof Error) {
    return true;
  }
  return false;
}

export function createFallback(onFallback?: (model: string) => void): Fallback {
  const trips = new Map<string, ProviderTrip>();

  const isTripped = (provider: string): boolean => {
    const t = trips.get(provider);
    if (!t) return false;
    if (Date.now() < t.trippedUntil) return true;
    trips.delete(provider);
    return false;
  };

  const recordFailure = (provider: string) => {
    const prev = trips.get(provider) ?? { failures: 0, trippedUntil: 0 };
    const failures = prev.failures + 1;
    if (failures >= 3) {
      trips.set(provider, { failures, trippedUntil: Date.now() + 5 * 60 * 1000 });
    } else {
      trips.set(provider, { failures, trippedUntil: 0 });
    }
  };

  const recordSuccess = (provider: string) => {
    trips.delete(provider);
  };

  return {
    async execute<T>(chain: FallbackChain, fn: (model: string) => Promise<T>): Promise<T> {
      const models = [chain.primary, ...chain.fallbackChain];
      const maxRetries = chain.maxRetries ?? 1;
      let lastError: unknown;

      for (let i = 0; i < models.length; i++) {
        const model = models[i]!;
        const provider = model.split("/")[0] ?? model;

        if (isTripped(provider)) {
          continue;
        }

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const result = await fn(model);
            recordSuccess(provider);
            if (i > 0) onFallback?.(model);
            return result;
          } catch (error) {
            lastError = error;
            if (!isRetriableError(error)) throw error;
            recordFailure(provider);
          }
        }
      }

      throw lastError ?? new Error("All models in fallback chain failed");
    },
  };
}
